import { gql, useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';
import { Result } from 'true-myth';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import { fieldDefsToFields } from '../../../../../../Utils/columnDefsToFields';
import mergeSort from '../../../../../../Utils/mergeSort';
import {
  IBusinessCollectionQueryResult,
  ICollectionDataType,
} from './BusinessCollectionQuery';
import { toString } from 'ramda';
import { ActiveRoutineContext } from '../../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IUIUrval } from '../../../../../../Types/UrvalTypes';

export interface IBusinessCollectionQueryProps {
  databaseName: string | null;
  collectionName: string;
  queryName?: string;
  columns: readonly string[];
  urval?: IUIUrval | null;
  urvalId?: string;
  routineID?: number;
  onCompleted?: (data: any) => void;
  networkOnlyOnFirst?: boolean;
  skip?: boolean;
}
// skapa query för att hämta poster i en collection
export const collectionQuery = function businessCollectionQuery(
  databaseName: string,
  collectionName: string,
  columns: readonly string[],
  routineID?: number,
  queryName?: string
): string {
  let dbargs = `name: "${databaseName}"`;
  if (routineID !== undefined) {
    dbargs = dbargs + ` routine: ${routineID}`;
  }
  const args = ` (selection: $userSelection, selectionID: $SelectionID)`;
  return `query ${
    queryName ?? 'collection'
  } ($userSelection: [[TEDInputSelectionCondition!]], $SelectionID: ID) {
    db(${dbargs}) {
      ${collectionName}_All${args} {
        ${fieldDefsToFields(collectionName)(columns || [])}
      }
    }
  }`;
};

/**Typar data eftersom vi vet att det alltid är en collection vi frågar efter */
export const dataAsCollectionData = (
  data: any,
  collectionName: string
): ICollectionDataType[] | null =>
  data === null ||
  data === undefined ||
  typeof data !== 'object' ||
  data.db == null ||
  typeof data.db !== 'object' ||
  data.loading === true
    ? []
    : (data.db[`${collectionName}_All`] as ICollectionDataType[]);

const errormessage = (querystring: string, error: string) => {
  return `Fel vid parsning av query:
  ${querystring}
  
  Felmeddelande från parser:
  ${error}`;
};

export const makeQuery = (
  databaseName: string,
  collectionName: string,
  columns: readonly string[],
  routineID?: number,
  queryName?: string
): Result<any, string> => {
  const querystring = collectionQuery(
    databaseName,
    collectionName,
    mergeSort(columns as string[]),
    routineID,
    queryName
  );
  let query;
  try {
    query = gql(querystring);
  } catch (error) {
    return Result.err(errormessage(querystring, toString(error)));
  }
  return Result.ok(query);
};

export const useBusinessCollectionQuery = (
  props: IBusinessCollectionQueryProps
): IBusinessCollectionQueryResult => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const activeRoutineContext = useContext(ActiveRoutineContext);

  const finalDatabaseName =
    props.databaseName ?? activeDatabaseContext.activeDatabase ?? '';
  const finalRoutineID =
    props.routineID ?? activeRoutineContext?.rutinInfo.rutinID;

  const query = useMemo(
    () =>
      makeQuery(
        finalDatabaseName || '',
        props.collectionName,
        props.columns,
        finalRoutineID,
        props.queryName
      ),
    [
      finalDatabaseName,
      props.collectionName,
      props.columns,
      finalRoutineID,
      props.queryName,
    ]
  );
  const variables = useMemo(
    () => ({
      userSelection: tryConvertUIUrvalToTEDUrval(props.urval),
      SelectionID: props.urvalId,
    }),
    [props.urval, props.urvalId]
  );

  const skip =
    !query.isOk() ||
    (!props.urval && !props.urvalId) ||
    !finalDatabaseName ||
    activeRoutineContext?.activeRoutineQueryLoading ||
    props.skip;

  // just a dummy-query to please useQuery typing when !query.isOk(), skip should always be true when this happens so should never send this query
  const dummyQuery = gql`
    query DummyQuery {
      me {
        sign
      }
    }
  `;

  const { loading, error, data, refetch } = useQuery(
    query.isOk() ? query.value : dummyQuery,
    {
      variables,
      onCompleted: props.onCompleted,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: props.networkOnlyOnFirst ? 'network-only' : undefined,
      nextFetchPolicy: props.networkOnlyOnFirst ? 'cache-first' : undefined,
      skip,
    }
  );

  const childProps: IBusinessCollectionQueryResult = useMemo(
    () => ({
      data: dataAsCollectionData(data, props.collectionName),
      refetch,
      loading,
      query,
      serverError: error,
    }),
    [data, refetch, loading, props.collectionName, query, error]
  );

  if (skip) {
    return {
      data: [],
      refetch: () => {},
      loading: false,
      query,
      serverError: error,
    };
  }

  return childProps;
};
